import { Deal } from '@funkey/constants';
import type { I18n } from '@funkey/locales';
import type { IconName } from '~/components/primitives';
import { z } from 'zod';
import type { FormState } from '~/modules/form';

/** Kept apart from `CheckoutSourceFieldset.tsx` so it stays importable without React. */
export namespace CheckoutSourceFormFields {
    export type FormValues = {
        checkoutSource: Deal.CheckoutSource | null;
        checkoutSourceOther: string;
    };

    export const DEFAULT_VALUES: FormValues = {
        checkoutSource: null,
        checkoutSourceOther: '',
    };

    /** The `hint` is rendered between brackets in smaller, lighter print. */
    export function option(
        i18n: I18n,
        source: Deal.CheckoutSource,
    ): { text: string; hint: string | null; icons: readonly IconName[] } {
        switch (source) {
            case 'SEARCH_ENGINE':
                return {
                    text: i18n._('checkout.source.search-engine'),
                    hint: i18n._('checkout.source.search-engine.hint'),
                    icons: ['google'],
                };
            case 'AI_CHAT':
                return {
                    text: i18n._('checkout.source.ai-chat'),
                    hint: i18n._('checkout.source.ai-chat.hint'),
                    icons: ['chat'],
                };
            case 'YOUTUBE_AD':
                return {
                    text: i18n._('checkout.source.youtube-ad'),
                    hint: null,
                    icons: ['play'],
                };
            case 'SOCIAL_MEDIA':
                return {
                    text: i18n._('checkout.source.social-media'),
                    hint: i18n._('checkout.source.social-media.hint'),
                    icons: ['linkedin', 'instagram', 'facebook'],
                };
            case 'WORD_OF_MOUTH':
                return {
                    text: i18n._('checkout.source.word-of-mouth'),
                    hint: i18n._('checkout.source.word-of-mouth.hint'),
                    icons: ['people-users'],
                };
            case 'RETURNING_CUSTOMER':
                return {
                    text: i18n._('checkout.source.returning-customer'),
                    hint: i18n._('checkout.source.returning-customer.hint'),
                    icons: ['history'],
                };
            case 'OTHER':
                return {
                    text: i18n._('checkout.source.other'),
                    hint: null,
                    icons: ['message'],
                };
            default:
                source satisfies never;
                throw new Error('UNREACHABLE');
        }
    }

    export function FORM_VALIDATION(i18n: I18n): FormState.Validation<FormValues> {
        return {
            checkoutSource: {
                validate: value => z.enum(Deal.CHECKOUT_SOURCES).safeParse(value).success,
                message: i18n._('checkout.source.error.required'),
            },
        };
    }

    /** `null` when nothing was picked. */
    export function toInput(values: FormValues) {
        if (!values.checkoutSource) return null;

        return {
            value: values.checkoutSource,
            other: values.checkoutSource === 'OTHER' ? values.checkoutSourceOther : null,
        };
    }

    /** The question is only asked in the public checkout. The contact form shares
     * `deal.create` and never sends one, so the rule keys on `source`, not the portal. */
    export function apiIssue(input: {
        portal: 'PUBLIC' | 'BIZZ';
        source: 'CHECKOUT' | 'CONTACT_FORM' | 'ZOHO_CRM';
        checkoutSource: unknown;
    }): string | null {
        if (input.portal === 'BIZZ') {
            return input.checkoutSource
                ? 'Checkout source is not collected on the Bizz portal'
                : null;
        }

        if (input.source === 'CHECKOUT' && !input.checkoutSource) {
            return 'Checkout source is required';
        }

        return null;
    }
}
